import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProgramGoalTimeline from '../../components/programs/ProgramGoalTimeline';
import QuoteTestimonials, {
  QUOTE_TESTIMONIALS
} from '../../components/common/QuoteTestimonials';
import {
  CREATE_ACCOUNT_LOP4UKER_URL,
  LOP4UKER_PROGRAM_ID,
  LOP4UKER_SIGNUP_CAMPAIGN,
  getLop4UkerProgramDeepLink
} from '../../utils/programDeepLinks';
import { tryOpenAppThenStore } from '../../utils/appStoreLinks';
import { appendUtmToUrl, parseUtmParams } from '../../utils/utmTracking';
import './Lop30MinutterLanding.css';
import './Lop4UkerLanding.css';

const LOP4UKER_TESTIMONIAL_AVATARS = [
  '/img/program/4week/skogsvann.png',
  '/img/program/4week/fjorden.png',
  '/img/program/4week/lopevest.png'
];

const lop4UkerTestimonials = QUOTE_TESTIMONIALS.map((item, index) => ({
  ...item,
  name: item.name === 'Johan' ? 'Amanda' : item.name,
  avatar: LOP4UKER_TESTIMONIAL_AVATARS[index] || item.avatar
}));

/** Default UTM when the landing is opened without utm_source in the URL */
const DEFAULT_LOP4UKER_UTM = {
  utm_source: LOP4UKER_SIGNUP_CAMPAIGN,
  utm_medium: 'landing',
  utm_campaign: LOP4UKER_SIGNUP_CAMPAIGN
};

/** Prefer live URL UTMs when utm_source is present; otherwise attribute to this landing. */
const getButtonUtmParams = () => {
  if (typeof window === 'undefined') return DEFAULT_LOP4UKER_UTM;
  const fromUrl = parseUtmParams(window.location.search);
  if (fromUrl.utm_source) return fromUrl;
  return DEFAULT_LOP4UKER_UTM;
};

const planCards = [
  {
    week: 'Uke 1–2',
    title: 'Oppstart og oppbygging',
    bullets: [
      '2 gå-økter',
      '1 gå/løp-økt nivå 1',
      '1 pusteøkt',
      '1 styrke med strikk'
    ],
    text: 'Start rolig med gange, pust og små gå/løp-steg – uten press.',
    image: '/img/program/bakkestranda2.jpg'
  },
  {
    week: 'Uke 3–4',
    title: 'Videreutvikling og fremgang',
    bullets: [
      '1 gå-økt',
      '1 gå-økt med motbakkeintervall',
      '1 gå/løpeøkt nivå 2',
      '1 pusteøkt',
      '1 styrke med strikk'
    ],
    text: 'Bygg videre med litt mer utfordring – fortsatt trygt og motiverende.',
    image: '/img/program/bakkestranda1.jpg'
  }
];

const valueCards = [
  {
    title: 'Pusteøvelse',
    subtitle: '1 gang i uka (gjerne hver dag)',
    ingress:
      'I denne 5-minutters øvelsen trener du på å la pusten slippe til, i stedet for å styre den. Med ekstra fokus på utpusten får kroppen hjelp til å roe ned, slippe spenninger og finne en friere pust.',
    icon: 'fas fa-spa',
    bullets: [
      'Roligere og friere pust',
      'Mindre spenning i kroppen',
      'Bedre restitusjon',
      'Enklere å finne pusten når du løper'
    ],
    image: '/img/program/lg-mindfullness.jpg',
    catchPhrase: 'Korte økter som gjør en stor forskjell'
  },
  {
    title: 'Styrke for løping',
    subtitle: '3 ganger i uka',
    ingress:
      'Du får både en økt på øret på 8 min som du tar der og da uten hjelpemidler. I tillegg kan du ta styrke med strikk 10 min 1 gang i uka. Ønsker du ta den 2–3 ganger er det supert.',
    icon: 'fas fa-dumbbell',
    bullets: [
      'Sterkere bein og sete',
      'Mindre risiko for skader',
      'Bedre stabilitet i kroppen',
      'Mer energi i hverdagen'
    ],
    image: '/img/program/lg-strikk.jpg',
    catchPhrase: 'Bygget for å gjøre deg til en bedre løper'
  },
  {
    title: 'Gå- og gå/løp-økter',
    subtitle: 'Korte økter på rundt 15 min',
    ingress:
      'Et lavterskel opplegg der du nærmer deg løping med korte intervaller – fra 10 til 90 sekunder av gangen. Du bygger kondisjon, rytme og trygghet uten å presse deg.',
    icon: 'fas fa-running',
    bullets: [
      'Trygg start fra sofaen',
      'Riktig intensitet for fremgang',
      'Varierte og motiverende økter',
      'Bygget for å passe en travel hverdag'
    ],
    image: '/img/program/lg-stress.jpg',
    catchPhrase: 'Du får en plan – vi heier på deg hele veien'
  }
];

const LOGO_SRC = '/lopeglad_white.png';
const FOOTER_LOGO_SRC = '/lopeglad_white.png';

const heroFeatures = [
  'Ubegrenset tilgang til økter',
  'Lag dine egne spillelister',
  'Finn ditt nivå',
  'Lagre dine favoritter'
];

const trustBarItems = [
  { icon: 'fas fa-layer-group', label: 'Tilpasset ditt nivå' },
  { icon: 'fas fa-users', label: 'Norsk stemme' },
  { icon: 'fas fa-stopwatch', label: 'Tren når det passer deg' },
  { icon: 'fas fa-tag', label: 'Trygt og effektivt' },
  { icon: 'fas fa-shield-alt', label: 'Bygget for varige resultater' }
];

/** 149 NOK in øre/cents for Fathom _value */
const TRIAL_CTA_VALUE_CENTS = 14900;

const trackFathomEvent = (eventName, options) => {
  if (typeof window === 'undefined' || typeof window.fathom?.trackEvent !== 'function') {
    return;
  }
  if (options) {
    window.fathom.trackEvent(eventName, options);
  } else {
    window.fathom.trackEvent(eventName);
  }
};

const Lop4UkerLanding = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = `app-id=6741588236, app-argument=${getLop4UkerProgramDeepLink()}`;
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const trackStartTrial = () => {
    trackFathomEvent('Start gratis i 7 dager', { _value: TRIAL_CTA_VALUE_CENTS });
  };

  const buttonUtm = getButtonUtmParams();
  const createAccountUrl = appendUtmToUrl(CREATE_ACCOUNT_LOP4UKER_URL, buttonUtm);

  const handleOpenInApp = (event) => {
    event.preventDefault();
    trackFathomEvent('Åpne programmet i appen');
    tryOpenAppThenStore(appendUtmToUrl(getLop4UkerProgramDeepLink(), getButtonUtmParams()));
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className={`lop30 lop4uker${menuOpen ? ' lop30--menu-open' : ''}`}>
      <header className="lop30-header">
        <div className="lop30-container">
          <div className="lop30-header-inner">
            <div className="lop30-header-brand">
              <Link to="/" onClick={closeMenu}>
                <img src={LOGO_SRC} alt="Løpeglad" className="lop30-logo" />
              </Link>
            </div>
            <nav className="lop30-nav lop30-nav--desktop" aria-label="Hovedmeny">
              <a href="#appen">Appen</a>
              <a href="#om">Om Løpeglad</a>
              <a href="https://open.spotify.com/show/2FbsnJU03WoIsViXBx97st">Podcast</a>
            </nav>
            <div className="lop30-header-actions">
              <div className="lop30-header-cta">
                <Link
                  to={createAccountUrl}
                  className="lop30-btn lop30-btn--header-cta"
                  onClick={trackStartTrial}
                >
                  Start gratis i 7 dager
                </Link>
              </div>
              <button
                type="button"
                className={`lop30-menu-toggle${menuOpen ? ' is-open' : ''}`}
                aria-expanded={menuOpen}
                aria-controls="lop4uker-mobile-menu"
                aria-label={menuOpen ? 'Lukk meny' : 'Åpne meny'}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span className="lop30-menu-toggle__bars" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          className={`lop30-menu-backdrop${menuOpen ? ' is-open' : ''}`}
          aria-hidden={!menuOpen}
          onClick={closeMenu}
        />

        <div
          id="lop4uker-mobile-menu"
          className={`lop30-mobile-menu${menuOpen ? ' is-open' : ''}`}
          aria-hidden={!menuOpen}
          {...(!menuOpen ? { inert: '' } : {})}
        >
          <nav className="lop30-mobile-menu__nav" aria-label="Mobilmeny">
            <a href="#appen" onClick={closeMenu}>Appen</a>
            <a href="#om" onClick={closeMenu}>Om Løpeglad</a>
            <a
              href="https://open.spotify.com/show/2FbsnJU03WoIsViXBx97st"
              onClick={closeMenu}
            >
              Podcast
            </a>
            <Link
              to={`/programs/${LOP4UKER_PROGRAM_ID}`}
              className="lop30-mobile-menu__program"
              onClick={closeMenu}
            >
              Se hele programmet
            </Link>
          </nav>
          <Link
            to={createAccountUrl}
            className="lop30-btn lop30-btn--cta lop30-btn--cta-lg lop30-mobile-menu__cta"
            onClick={() => {
              trackStartTrial();
              closeMenu();
            }}
          >
            Start gratis i 7 dager
          </Link>
        </div>
      </header>

      <section className="lop30-hero">
        <div className="lop30-hero-bg" aria-hidden="true">
          <img src="/img/program/4week/skogsvann.png" alt="" />
        </div>
        <div className="lop30-hero-overlay" aria-hidden="true" />
        <div className="lop30-container lop30-hero-inner">
          <div className="lop30-hero-copy">
            <span className="lop30-badge">4 uker – steg for steg</span>
            <h1>Gi kroppen en trygg og motiverende start på løping</h1>
            <p className="lop30-hero-tagline">
              – uten stress, press og følelsen av å «mislykkes»
            </p>
            <p className="lop30-hero-desc">
              Et steg-for-steg-program som tar deg fra «sofaen» til å kjenne spiren av løpeglede.
            </p>
            <div className="lop30-hero-actions">
              <Link
                to={createAccountUrl}
                className="lop30-btn lop30-btn--cta lop30-btn--cta-lg"
                onClick={trackStartTrial}
              >
                Start gratis i 7 dager
              </Link>

              <button
                type="button"
                className="lop30-btn lop30-btn--outline"
                onClick={handleOpenInApp}
              >
                <i className="fas fa-mobile-alt" aria-hidden="true" />
                Åpne programmet i appen
              </button>
            </div>
            <div className="lop30-hero-trust-row">
              <div className="lop30-trust">
                <span><i className="far fa-calendar-check" aria-hidden="true" />Ingen bindingstid</span>
                <span><i className="fas fa-music" aria-hidden="true" />Tilpasset musikk på frekvens</span>
                <span><i className="fas fa-headphones" aria-hidden="true" />Norsk stemme</span>
              </div>
              <div className="lop30-hero-price-badge lop30-hero-price-badge--mobile">
                <p className="lop30-hero-price-label">Full tilgang</p>
                <p className="lop30-hero-price-amount">149 kr</p>
                <p className="lop30-hero-price-period">/ mnd</p>
              </div>
            </div>
          </div>
          <aside className="lop30-hero-aside" aria-label="Pris og fordeler">
            <div className="lop30-hero-price-badge lop30-hero-price-badge--desktop">
              <p className="lop30-hero-price-label">Full tilgang</p>
              <p className="lop30-hero-price-amount">149 kr</p>
              <p className="lop30-hero-price-period">/ mnd</p>
            </div>
            <div className="lop30-hero-features-box">
              <ul className="lop30-hero-features-list">
                {heroFeatures.map((feature) => (
                  <li key={feature}>
                    <span className="lop30-hero-feature-icon" aria-hidden="true">
                      <i className="fas fa-check-circle" />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="lop30-section">
        <div className="lop30-container">
          <h2 className="lop30-section-title">Programmet: 4 uker «kom i gang»</h2>
          <p className="lop30-section-subtitle">
            Hver uke har økter som hjelper kondisjon, styrke og pust. Programmet gir deg
            kroppskunnskap, vekker motivasjon og lærer deg nyttig treningsdosering.
          </p>

          <div className="lop30-program-cta">
            <Link
              to={`/programs/${LOP4UKER_PROGRAM_ID}`}
              className="lop30-btn lop30-btn--cta lop30-btn--cta-lg"
            >
              Se hele programmet
            </Link>
          </div>
          <div className="lop30-program-grid">
            {planCards.map((card, index) => (
              <React.Fragment key={card.title}>
                {index > 0 && (
                  <div className="lop30-program-arrow" aria-hidden="true">
                    <i className="fas fa-arrow-right" />
                  </div>
                )}
                <article className="lop30-program-card">
                  <div className="lop30-program-card-media">
                    <img src={card.image} alt="" />
                    <span className="lop30-program-tag">{card.week}</span>
                  </div>
                  <div className="lop30-program-body">
                    <h3>{card.title}</h3>
                    <ul className="lop30-check-list">
                      {card.bullets.map((bullet) => (
                        <li key={bullet}>
                          <i className="far fa-check-circle" aria-hidden="true" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                    <p className="lop30-program-text">
                      {card.text}
                    </p>
                  </div>
                </article>
              </React.Fragment>
            ))}
          </div>
          <ProgramGoalTimeline label="Målet: kjenne spiren av løpeglede" />
          <div className="lop30-video-block">
            <h2 className="lop30-section-title">Se video</h2>
            <div className="lop30-video-frame">
              <video
                className="lop30-video"
                controls
                playsInline
                preload="metadata"
                aria-label="Løpeglad høstkampanje 2026"
              >
                <source src="/video/lopeglad_hostkampanje_2026_4K.mp4" type="video/mp4" />
                Videoen kan ikke spilles av i denne nettleseren.
              </video>
            </div>
          </div>
        </div>
      </section>

      <section className="lop30-section" id="om">
        <div className="lop30-container">
          <h2 className="lop30-section-title">Mer enn bare et løpeprogram</h2>
          <p className="lop30-section-subtitle">
            I Løpeglad får du verktøyene du trenger for å lykkes – på dine premisser.
          </p>
          <div className="lop30-value-grid">
            {valueCards.map((card) => (
              <article key={card.title} className="lop30-value-card">
                <div className="lop30-value-card-media">
                  <img src={card.image} alt="" />
                  <span className="lop30-value-icon" aria-hidden="true">
                    <i className={card.icon} />
                  </span>
                </div>
                <div className="lop30-value-body">
                  <h3>{card.title}</h3>
                  <p className="lop30-value-sub">{card.subtitle}</p>
                  <p className="lop30-value-ingress">{card.ingress}</p>
                  <ul className="lop30-check-list">
                    {card.bullets.map((bullet) => (
                      <li key={bullet}>
                        <i className="fas fa-check" aria-hidden="true" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <p className="lop30-value-catch-phrase">{card.catchPhrase}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lop30-section">
        <div className="lop30-container">
          <QuoteTestimonials
            title="Dette sier våre medlemmer"
            titleClassName="lop30-section-title"
            items={lop4UkerTestimonials}
          />
        </div>
      </section>

      <section className="lop30-section lop30-cta-section" id="appen">
        <div className="lop30-container">
          <div className="lop30-cta-block">
            <div className="lop30-cta-copy">
              <h2>Klar for å starte reisen?</h2>
              <p>
                Start gratis i 7 dager og få tilgang til hele programmet i appen – helt uten bindingstid.
              </p>
            </div>
            <div className="lop30-cta-action">
              <Link
                to={createAccountUrl}
                className="lop30-btn lop30-btn--cta-banner"
                onClick={trackStartTrial}
              >
                Start gratis i 7 dager
                <span className="lop30-cta-arrow" aria-hidden="true">→</span>
              </Link>

              <button
                type="button"
                className="lop30-btn lop30-btn--outline lop30-btn--open-app"
                onClick={handleOpenInApp}
              >
                <i className="fas fa-mobile-alt" aria-hidden="true" />
                Åpne programmet i appen
              </button>
              <p className="lop30-cta-note">
                <i className="fas fa-lock" aria-hidden="true" />
                Avslutt når du vil. Ingen bindingstid.
              </p>
            </div>
            <div className="lop30-store-badges">
              <a
                href="https://apps.apple.com/no/app/l%C3%B8peglad/id6741588236?l=nb"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/img/AppStore.png" alt="Last ned på App Store" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.app.lopeglad"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/img/GooglePlayNorwegian.png" alt="Last ned på Google Play" />
              </a>
            </div>
          </div>
          <div className="lop30-trust-bar">
            {trustBarItems.map((item) => (
              <span key={item.label}>
                <i className={item.icon} aria-hidden="true" />
                {item.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <footer className="lop30-site-footer">
        <div className="lop30-container lop30-site-footer-inner">
          <Link to="/" className="lop30-site-footer-logo">
            <img src={FOOTER_LOGO_SRC} alt="Løpeglad" />
          </Link>
          <nav className="lop30-site-footer-nav" aria-label="Bunntekst">
            <a href="#appen">Appen</a>
            <a href="#om">Om Løpeglad</a>
            <a href="https://open.spotify.com/show/2FbsnJU03WoIsViXBx97st">Podcast</a>
          </nav>
          <div className="lop30-social lop30-social--footer">
            <a href="https://www.instagram.com/lopeglad.no/" aria-label="Instagram">
              <i className="fab fa-instagram" />
            </a>
            <a href="https://www.facebook.com/lopeglad.no" aria-label="Facebook">
              <i className="fab fa-facebook" />
            </a>
          </div>
        </div>
      </footer>

      {!menuOpen && (
        <Link
          to={createAccountUrl}
          className="lop30-sticky-cta"
          onClick={trackStartTrial}
        >
          Start gratis 7 dager
        </Link>
      )}
    </div>
  );
};

export default Lop4UkerLanding;
